<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useChatStore } from "@/stores/chat";
import ChatMessage from "@/components/ChatMessage.vue";

const { t } = useI18n();
const chat = useChatStore();

const draft = ref("");
const newName = ref("");
const newPreset = ref("db-ops");
const addPick = ref("");
const channelsLoading = ref(true);
const stream = ref<HTMLElement | null>(null);

// Org workers not already in the active channel — the "add participant" options.
const availableWorkers = computed(() =>
  chat.workers.filter((w) => !chat.members.some((m) => m.id === w.id)),
);

async function addWorker() {
  if (addPick.value) {
    await chat.addMember(addPick.value);
    addPick.value = "";
  }
}

async function scrollDown() {
  await nextTick();
  if (stream.value) stream.value.scrollTop = stream.value.scrollHeight;
}

watch(() => chat.messages.length, scrollDown);

async function send() {
  const text = draft.value.trim();
  if (!text) return;
  draft.value = "";
  await chat.post(text);
}

async function create() {
  const name = newName.value.trim();
  if (!name) return;
  const ch = await chat.createChannel(name, newPreset.value || undefined);
  newName.value = "";
  await chat.selectChannel(ch.id);
}

onMounted(async () => {
  try {
    await chat.loadChannels();
    await chat.loadWorkers();
  } finally {
    channelsLoading.value = false;
  }
  if (chat.channels.length) await chat.selectChannel(chat.channels[0].id);
});
onUnmounted(() => chat.stop());
</script>

<template>
  <div class="chat">
    <aside class="chat__sidebar">
      <h2>{{ t("chat.channels") }}</h2>
      <div v-if="channelsLoading" class="loading-note" role="status" aria-live="polite">
        <span class="spinner" aria-hidden="true"></span> {{ t("admin.loading") }}
      </div>
      <ul v-else class="chat__channels">
        <li
          v-for="c in chat.channels"
          :key="c.id"
          role="button"
          tabindex="0"
          :aria-current="c.id === chat.activeId ? 'true' : undefined"
          :class="{ active: c.id === chat.activeId }"
          @click="chat.selectChannel(c.id)"
          @keydown.enter.prevent="chat.selectChannel(c.id)"
          @keydown.space.prevent="chat.selectChannel(c.id)"
        >
          # {{ c.name }}
          <span v-if="c.worker_preset" class="chat__preset">{{ c.worker_preset }}</span>
        </li>
      </ul>
      <form class="chat__new" @submit.prevent="create">
        <input v-model="newName" :placeholder="t('chat.new_channel')" />
        <input v-model="newPreset" :placeholder="t('chat.worker_preset')" />
        <button type="submit">{{ t("chat.create") }}</button>
      </form>
    </aside>

    <main class="chat__main">
      <div v-if="!chat.activeId" class="chat__empty">{{ t("chat.pick_channel") }}</div>
      <template v-else>
        <header class="chat__worker" data-test="worker-bar">
          <span v-if="!chat.members.length" class="chat__worker-empty">
            {{ t("chat.no_members") }}
          </span>
          <span
            v-for="m in chat.members"
            :key="m.id"
            class="chat__participant"
            :data-test="`member-${m.id}`"
          >
            <span class="chat__worker-dot" :data-status="m.status"></span>
            <span class="chat__participant-name">{{ m.name }}</span>
            <span class="chat__participant-status" :data-test="`member-status-${m.id}`">
              {{ t(`chat.worker_${m.status}`) }}
            </span>
            <button
              type="button"
              class="chat__worker-btn"
              :data-test="
                m.status === 'paused' ? `member-resume-${m.id}` : `member-pause-${m.id}`
              "
              :disabled="m.status === 'offline'"
              @click="chat.controlWorker(m.id, m.status === 'paused' ? 'resume' : 'pause')"
            >
              {{ m.status === "paused" ? t("chat.worker_resume") : t("chat.worker_pause") }}
            </button>
            <button
              type="button"
              class="chat__worker-btn"
              :data-test="`member-stop-${m.id}`"
              :disabled="m.status === 'offline'"
              @click="chat.controlWorker(m.id, 'stop')"
            >
              {{ t("chat.worker_stop") }}
            </button>
            <button
              type="button"
              class="chat__worker-btn"
              :data-test="`member-restart-${m.id}`"
              :disabled="m.status === 'offline'"
              @click="chat.controlWorker(m.id, 'restart')"
            >
              {{ t("chat.worker_restart") }}
            </button>
            <button
              type="button"
              class="chat__worker-x"
              :data-test="`member-remove-${m.id}`"
              :title="t('chat.member_remove')"
              :aria-label="t('chat.member_remove')"
              @click="chat.removeMember(m.id)"
            >
              ✕
            </button>
          </span>
          <select
            v-if="availableWorkers.length"
            v-model="addPick"
            class="chat__add-worker"
            data-test="add-worker"
            @change="addWorker"
          >
            <option value="">{{ t("chat.add_worker") }}</option>
            <option v-for="w in availableWorkers" :key="w.id" :value="w.id">{{ w.name }}</option>
          </select>
        </header>
        <div ref="stream" class="chat__stream">
          <ChatMessage
            v-for="m in chat.messages"
            :key="m.id"
            :message="m"
            @approve="(id, d) => chat.approve(id, d)"
          />
        </div>
        <form class="chat__composer" @submit.prevent="send">
          <textarea
            v-model="draft"
            :placeholder="t('chat.composer_placeholder')"
            rows="2"
            @keydown.enter.exact.prevent="send"
          ></textarea>
          <button type="submit">{{ t("chat.send") }}</button>
        </form>
      </template>
      <p v-if="chat.error" class="admin-error">{{ chat.error }}</p>
    </main>
  </div>
</template>

<style scoped>
.chat {
  display: flex;
  height: calc(100vh - 60px);
  min-height: 0;
}
.chat__sidebar {
  flex: 0 0 240px;
  border-right: 1px solid var(--color-border);
  padding: 0.75rem;
  overflow-y: auto;
}
.chat__sidebar h2 {
  font-size: 0.9rem;
  margin: 0 0 0.5rem;
}
.chat__channels {
  list-style: none;
  margin: 0 0 1rem;
  padding: 0;
}
.chat__channels li {
  padding: 0.4rem 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
}
.chat__channels li:hover {
  background: var(--color-bg-elev);
}
.chat__channels li.active {
  background: var(--color-bg-elev);
  font-weight: 600;
}
.chat__preset {
  font-size: 0.7rem;
  color: var(--color-fg-dim);
  margin-left: 0.35rem;
}
.chat__new {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.chat__new input,
.chat__composer textarea {
  padding: 0.4rem 0.5rem;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  background: var(--color-bg);
  color: var(--color-fg);
  font: inherit;
}
.chat__main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 0.75rem;
}
.chat__stream {
  flex: 1;
  overflow-y: auto;
  padding-right: 0.5rem;
}
.chat__empty {
  margin: auto;
  color: var(--color-fg-dim);
}
.chat__composer {
  display: flex;
  gap: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--color-border);
}
.chat__composer textarea {
  flex: 1;
  resize: none;
}
.chat__composer button,
.chat__new button {
  background: var(--color-accent);
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 0.4rem 1rem;
  cursor: pointer;
}
.chat__worker {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.4rem 0.75rem;
  border-bottom: 1px solid var(--color-border);
  font-size: 0.85rem;
}
.chat__participant {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}
.chat__participant-name {
  font-weight: 600;
}
.chat__participant-status {
  opacity: 0.7;
}
.chat__worker-empty {
  opacity: 0.6;
}
.chat__worker-x {
  background: transparent;
  border: none;
  color: inherit;
  opacity: 0.5;
  cursor: pointer;
  padding: 0 0.2rem;
}
.chat__worker-x:hover {
  opacity: 1;
}
.chat__add-worker {
  margin-left: auto;
  font-size: 0.8rem;
}
.chat__worker-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--color-fg-dim);
}
.chat__worker-dot[data-status="idle"] {
  background: var(--color-ok);
}
.chat__worker-dot[data-status="working"] {
  background: var(--color-warn);
}
.chat__worker-dot[data-status="paused"] {
  background: var(--color-accent);
}
.chat__worker-spacer {
  flex: 1;
}
.chat__worker-btn {
  background: transparent;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 0.2rem 0.6rem;
  cursor: pointer;
  color: inherit;
}
.chat__worker-btn:disabled {
  opacity: 0.4;
  cursor: default;
}
</style>
